import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Web Worker 메시지 타입
 */
export type WorkerMessageType =
  | "STRINGIFY"
  | "PARSE"
  | "STRINGIFY_AND_ENCODE"
  | "PARSE_AND_DECODE"
  | "STRINGIFY_AND_ENCODE_CHUNKED"
  | "PARSE_AND_DECODE_CHUNKED"
  | "COMPRESS_GZIP"
  | "DECOMPRESS_GZIP"
  | "STRINGIFY_ENCODE_COMPRESS"
  | "DECOMPRESS_DECODE_PARSE";

/**
 * 청크 진행 상황 정보
 */
export interface ChunkProgress {
  total: number;
  processed: number;
  chunkCount: number;
  currentChunk: number;
}

/**
 * Web Worker 응답 타입
 */
export interface WorkerResponse<T = unknown> {
  id: string;
  type: string;
  data?: T;
  error?: string;
}

/**
 * Web Worker 훅 반환 타입
 */
export interface UseWebWorkerReturn {
  /**
   * JSON.stringify를 Web Worker에서 실행
   */
  stringify: <T>(data: T) => Promise<string>;

  /**
   * JSON.parse를 Web Worker에서 실행
   */
  parse: <T>(jsonString: string) => Promise<T>;

  /**
   * JSON.stringify + Base64 인코딩을 Web Worker에서 실행
   */
  stringifyAndEncode: <T>(data: T) => Promise<{
    base64Data: string;
    byteLength: number;
  }>;

  /**
   * Base64 디코딩 + JSON.parse를 Web Worker에서 실행
   */
  parseAndDecode: <T>(base64Data: string) => Promise<T>;

  /**
   * 청크 단위로 JSON.stringify + Base64 인코딩 (10MB 이상 데이터용)
   * @param data - 처리할 데이터
   * @param onProgress - 진행 상황 콜백 (선택사항)
   */
  stringifyAndEncodeChunked: <T>(
    data: T,
    onProgress?: (progress: ChunkProgress) => void
  ) => Promise<{
    base64Data: string;
    byteLength: number;
    chunkCount: number;
  }>;

  /**
   * 청크 단위로 Base64 디코딩 + JSON.parse (10MB 이상 데이터용)
   * @param base64Data - Base64 인코딩된 데이터
   * @param onProgress - 진행 상황 콜백 (선택사항)
   */
  parseAndDecodeChunked: <T>(
    base64Data: string,
    onProgress?: (progress: ChunkProgress) => void
  ) => Promise<T>;

  /**
   * Gzip 압축
   * @param data - 압축할 데이터 (문자열 또는 Uint8Array)
   */
  compressGzip: (data: string | Uint8Array) => Promise<{
    compressed: Uint8Array;
    originalSize: number;
    compressedSize: number;
    ratio: string;
  }>;

  /**
   * Gzip 해제
   * @param compressed - 압축된 데이터 (Uint8Array)
   */
  decompressGzip: (compressed: Uint8Array) => Promise<{
    decompressed: string;
    originalSize: number;
    decompressedSize: number;
  }>;

  /**
   * JSON.stringify + Base64 인코딩 + Gzip 압축
   * @param data - 처리할 데이터
   */
  stringifyEncodeCompress: <T>(data: T) => Promise<{
    base64Data: string;
    compressedBase64: string;
    byteLength: number;
    originalSize: number;
    compressedSize: number;
    ratio: string;
  }>;

  /**
   * Gzip 해제 + Base64 디코딩 + JSON.parse
   * @param compressedBase64 - 압축된 Base64 데이터
   */
  decompressDecodeParse: <T>(compressedBase64: string) => Promise<T>;

  /**
   * CompressionStream API 지원 여부
   */
  isCompressionSupported: boolean;

  /**
   * Worker 준비 상태
   */
  isReady: boolean;

  /**
   * Worker 에러 상태
   */
  error: Error | null;
}

/**
 * Web Worker를 사용하여 대용량 JSON 처리를 메인 스레드에서 분리하는 훅
 *
 * @param workerPath - Web Worker 파일 경로 (기본값: '/workers/jsonWorker.js')
 * @returns Web Worker 함수들과 상태
 *
 * @example
 * ```tsx
 * const { stringifyAndEncode, isReady } = useWebWorker();
 *
 * useEffect(() => {
 *   if (isReady && largeData) {
 *     stringifyAndEncode(largeData).then(({ base64Data, byteLength }) => {  // jsonString 제거됨 (메모리 최적화)
 *       // 처리 완료
 *     });
 *   }
 * }, [isReady, largeData]);
 * ```
 */
export const useWebWorker = (
  workerPath: string = "/workers/jsonWorker.js"
): UseWebWorkerReturn => {
  const workerRef = useRef<Worker | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // CompressionStream API 지원 여부는 컴포넌트 마운트 시 한 번만 확인 (의존성 없음)
  const [isCompressionSupported] = useState(() => {
    return (
      typeof CompressionStream !== "undefined" &&
      typeof DecompressionStream !== "undefined"
    );
  });
  const pendingRequestsRef = useRef<
    Map<
      string,
      {
        resolve: (value: unknown) => void;
        reject: (error: Error) => void;
        onProgress?: (progress: ChunkProgress) => void;
      }
    >
  >(new Map());

  // Worker 초기화
  useEffect(() => {
    // Web Worker 지원 여부 확인
    if (typeof Worker === "undefined") {
      setError(new Error("Web Workers are not supported in this environment"));
      return;
    }

    try {
      const worker = new Worker(workerPath);
      workerRef.current = worker;

      // Worker 메시지 핸들러
      worker.onmessage = (e) => {
        const { id, type, data, error: errorMessage, progress } = e.data;

        if (type === "WORKER_READY") {
          setIsReady(true);
          setError(null);
          return;
        }

        // 청크 진행 상황 처리
        if (type === "CHUNK_PROGRESS") {
          const pendingRequest = pendingRequestsRef.current.get(id);
          if (pendingRequest && pendingRequest.onProgress) {
            pendingRequest.onProgress(progress);
          }
          return;
        }

        const pendingRequest = pendingRequestsRef.current.get(id);
        if (!pendingRequest) {
          if (process.env.NODE_ENV === "development") {
            console.warn(`⚠️ [WebWorker] 알 수 없는 요청 ID: ${id}`, e.data);
          }
          return;
        }

        pendingRequestsRef.current.delete(id);

        if (errorMessage) {
          pendingRequest.reject(new Error(errorMessage));
        } else {
          pendingRequest.resolve(data);
        }
      };

      // Worker 에러 핸들러
      worker.onerror = (e) => {
        const error = new Error(
          `Web Worker error: ${e.message || "Unknown error"}`
        );
        setError(error);
        setIsReady(false);

        // 모든 pending 요청 거부
        pendingRequestsRef.current.forEach(({ reject }) => {
          reject(error);
        });
        pendingRequestsRef.current.clear();

        if (process.env.NODE_ENV === "development") {
          console.error("❌ [WebWorker] Worker 에러:", e);
        }
      };

      // Worker 종료 핸들러
      worker.onmessageerror = (e) => {
        const error = new Error("Web Worker message error");
        setError(error);
        setIsReady(false);

        if (process.env.NODE_ENV === "development") {
          console.error("❌ [WebWorker] 메시지 에러:", e);
        }
      };
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Failed to initialize Web Worker");
      setError(error);
      setIsReady(false);

      if (process.env.NODE_ENV === "development") {
        console.error("❌ [WebWorker] 초기화 실패:", err);
      }
    }

    // Cleanup
    const pendingRequests = pendingRequestsRef.current;
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      pendingRequests.clear();
      setIsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerPath]); // workerPath가 변경될 때만 재초기화

  // 고유 ID 생성
  const generateId = useCallback(() => {
    return `worker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // JSON.stringify
  const stringify = useCallback(
    <T>(data: T): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as { jsonString: string };
            resolve(result.jsonString);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "STRINGIFY",
          payload: data,
        });
      });
    },
    [isReady, generateId]
  );

  // JSON.parse
  const parse = useCallback(
    <T>(jsonString: string): Promise<T> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as { data: T };
            resolve(result.data);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "PARSE",
          payload: jsonString,
        });
      });
    },
    [isReady, generateId]
  );

  // JSON.stringify + Base64 인코딩
  const stringifyAndEncode = useCallback(
    <T>(
      data: T
    ): Promise<{
      base64Data: string;
      byteLength: number;
    }> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as {
              base64Data: string;
              byteLength: number;
            };
            resolve(result);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "STRINGIFY_AND_ENCODE",
          payload: data,
        });
      });
    },
    [isReady, generateId]
  );

  // Base64 디코딩 + JSON.parse
  const parseAndDecode = useCallback(
    <T>(base64Data: string): Promise<T> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as { data: T };
            resolve(result.data);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "PARSE_AND_DECODE",
          payload: { base64Data },
        });
      });
    },
    [isReady, generateId]
  );

  // 청크 단위로 JSON.stringify + Base64 인코딩 (10MB 이상 데이터용)
  const stringifyAndEncodeChunked = useCallback(
    <T>(
      data: T,
      onProgress?: (progress: ChunkProgress) => void
    ): Promise<{
      base64Data: string;
      byteLength: number;
      chunkCount: number;
    }> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as {
              base64Data: string;
              byteLength: number;
              chunkCount: number;
            };
            resolve(result);
          },
          reject,
          onProgress,
        });

        workerRef.current.postMessage({
          id,
          messageType: "STRINGIFY_AND_ENCODE_CHUNKED",
          payload: { data },
        });
      });
    },
    [isReady, generateId]
  );

  // 청크 단위로 Base64 디코딩 + JSON.parse (10MB 이상 데이터용)
  const parseAndDecodeChunked = useCallback(
    <T>(
      base64Data: string,
      onProgress?: (progress: ChunkProgress) => void
    ): Promise<T> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as { data: T };
            resolve(result.data);
          },
          reject,
          onProgress,
        });

        workerRef.current.postMessage({
          id,
          messageType: "PARSE_AND_DECODE_CHUNKED",
          payload: { base64Data },
        });
      });
    },
    [isReady, generateId]
  );

  // Gzip 압축
  const compressGzip = useCallback(
    (
      data: string | Uint8Array
    ): Promise<{
      compressed: Uint8Array;
      originalSize: number;
      compressedSize: number;
      ratio: string;
    }> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as {
              compressed: number[];
              originalSize: number;
              compressedSize: number;
              ratio: string;
            };
            resolve({
              compressed: new Uint8Array(result.compressed),
              originalSize: result.originalSize,
              compressedSize: result.compressedSize,
              ratio: result.ratio,
            });
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "COMPRESS_GZIP",
          payload: {
            data: typeof data === "string" ? data : Array.from(data),
          },
        });
      });
    },
    [isReady, generateId]
  );

  // Gzip 해제
  const decompressGzip = useCallback(
    (
      compressed: Uint8Array
    ): Promise<{
      decompressed: string;
      originalSize: number;
      decompressedSize: number;
    }> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as {
              decompressed: string;
              originalSize: number;
              decompressedSize: number;
            };
            resolve(result);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "DECOMPRESS_GZIP",
          payload: {
            compressed: Array.from(compressed),
          },
        });
      });
    },
    [isReady, generateId]
  );

  // JSON.stringify + Base64 인코딩 + Gzip 압축
  const stringifyEncodeCompress = useCallback(
    <T>(
      data: T
    ): Promise<{
      base64Data: string;
      compressedBase64: string;
      byteLength: number;
      originalSize: number;
      compressedSize: number;
      ratio: string;
    }> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as {
              base64Data: string;
              compressedBase64: string;
              byteLength: number;
              originalSize: number;
              compressedSize: number;
              ratio: string;
            };
            resolve(result);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "STRINGIFY_ENCODE_COMPRESS",
          payload: { data },
        });
      });
    },
    [isReady, generateId]
  );

  // Gzip 해제 + Base64 디코딩 + JSON.parse
  const decompressDecodeParse = useCallback(
    <T>(compressedBase64: string): Promise<T> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || !isReady) {
          reject(
            new Error(
              "Web Worker is not ready. Please wait for initialization."
            )
          );
          return;
        }

        const id = generateId();
        pendingRequestsRef.current.set(id, {
          resolve: (response: unknown) => {
            const result = response as { data: T };
            resolve(result.data);
          },
          reject,
        });

        workerRef.current.postMessage({
          id,
          messageType: "DECOMPRESS_DECODE_PARSE",
          payload: { compressedBase64 },
        });
      });
    },
    [isReady, generateId]
  );

  return {
    stringify,
    parse,
    stringifyAndEncode,
    parseAndDecode,
    stringifyAndEncodeChunked,
    parseAndDecodeChunked,
    compressGzip,
    decompressGzip,
    stringifyEncodeCompress,
    decompressDecodeParse,
    isCompressionSupported,
    isReady,
    error,
  };
};
